import {
  Activity,
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  HeartPulse,
  Info,
  Stethoscope,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  cpotIndicators,
  criteriaMatches,
  flagLabels,
  functionalAssessment,
  painScaleRows,
  recommendations,
  routeByPath,
  workflowRoutes,
} from "../data/guideline";
import type { CpotIndicator, Recommendation, RecommendationStrength, VisibleRecommendation, WorkflowRoute, WorkflowRouteId } from "../data/types";
import {
  buildUrl,
  mergeWorkflowState,
  painBand,
  parseWorkflowState,
  supportedFlags,
  type EgfrBand,
  type OpioidChoice,
  type WorkflowFlag,
  type WorkflowState,
  type AllergyStatus,
} from "../lib/state";

type NavPatch = Partial<WorkflowState>;
type WorkflowStageId = Exclude<WorkflowState["stage"], "unknown">;
type PatientDetailControl = "age" | "egfr" | "sedation" | "opioid" | "pain" | "cpot";

interface RecommendationGroup {
  id: string;
  title: string;
  kind: "target" | "intervention";
  strength: RecommendationStrength;
  choiceGroup?: string;
  choiceValue?: OpioidChoice;
  optionValue?: string;
  items: VisibleRecommendation[];
  missingContext: string[];
  isNotApplicable: boolean;
  notApplicableReason: string;
  blockedReasons: string[];
}

interface InterventionBlock {
  id: string;
  choiceGroup?: string;
  groups: RecommendationGroup[];
}

const workflowNav = workflowRoutes.filter(
  (route): route is WorkflowRoute & { id: WorkflowStageId } => route.group === "workflow" && route.id !== "breakthrough-pain",
);
const toolsNav = workflowRoutes.filter((route) => route.group === "tool" && route.id !== "tools");
const safetyAcknowledgementKey = "cardiac-analgesia-guide:safety-acknowledged";
const patientDetailsExpandedStoragePrefix = "cardiac-analgesia-guide:patient-details-expanded:";
const strengthOrder: Record<RecommendationStrength, number> = { recommended: 0, consider: 1, caution: 2, escalate: 3 };
const allergyFlags: WorkflowFlag[] = [
  "paracetamolAllergy",
  "pregabalinAllergy",
  "gabapentinAllergy",
  "morphineAllergy",
  "oxycodoneAllergy",
  "fentanylAllergy",
  "alfentanilAllergy",
  "remifentanilAllergy",
  "ketamineAllergy",
  "dexmedetomidineAllergy",
  "magnesiumAllergy",
  "nsaidAllergy",
  "buprenorphineAllergy",
  "dihydrocodeineAllergy",
];
const clinicalFlags = supportedFlags.filter((flag) => !allergyFlags.includes(flag));
const allergyToOpioid: Partial<Record<WorkflowFlag, OpioidChoice>> = {
  morphineAllergy: "morphine",
  oxycodoneAllergy: "oxycodone",
  fentanylAllergy: "fentanyl",
  alfentanilAllergy: "alfentanil",
  remifentanilAllergy: "remifentanil",
};

function getCurrentRoute(pathname: string): WorkflowRoute {
  if (pathname === "/breakthrough-pain") {
    return routeByPath.get("/oral-analgesia")!;
  }
  return routeByPath.get(pathname) ?? routeByPath.get("/start")!;
}

function scrollToTop() {
  if (/jsdom/i.test(navigator.userAgent)) return;
  requestAnimationFrame(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  });
}

function useUrlWorkflow() {
  const [locationSnapshot, setLocationSnapshot] = useState(() => ({
    pathname: window.location.pathname === "/" ? "/start" : window.location.pathname,
    search: window.location.search,
  }));

  useEffect(() => {
    if (window.location.pathname === "/") {
      window.history.replaceState({}, "", "/start");
      setLocationSnapshot({ pathname: "/start", search: "" });
    }

    const onPopState = () => {
      setLocationSnapshot({
        pathname: window.location.pathname === "/" ? "/start" : window.location.pathname,
        search: window.location.search,
      });
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const state = useMemo(() => parseWorkflowState(locationSnapshot.search), [locationSnapshot.search]);
  const route = getCurrentRoute(locationSnapshot.pathname);

  const navigate = useCallback(
    (path: string, patch: NavPatch = {}, mode: "push" | "replace" = "push") => {
      const nextState = mergeWorkflowState(parseWorkflowState(window.location.search), patch);
      const url = buildUrl(path, { ...nextState, route: path });
      if (mode === "replace") {
        window.history.replaceState({}, "", url);
      } else {
        window.history.pushState({}, "", url);
      }
      setLocationSnapshot({ pathname: path, search: window.location.search });
      scrollToTop();
    },
    [],
  );

  const updateState = useCallback(
    (patch: NavPatch) => {
      const nextState = mergeWorkflowState(parseWorkflowState(window.location.search), patch);
      const path = window.location.pathname === "/" ? "/start" : window.location.pathname;
      const url = buildUrl(path, nextState);
      window.history.replaceState({}, "", url);
      setLocationSnapshot({ pathname: path, search: window.location.search });
    },
    [],
  );

  const reset = useCallback(() => {
    window.history.pushState({}, "", "/start");
    setLocationSnapshot({ pathname: "/start", search: "" });
    scrollToTop();
  }, []);

  return { route, state, navigate, updateState, reset };
}

export function App() {
  const { route, state, navigate, updateState } = useUrlWorkflow();
  const [showSafetyModal, setShowSafetyModal] = useState(() => localStorage.getItem(safetyAcknowledgementKey) !== "true");

  const acknowledgeSafety = useCallback(() => {
    localStorage.setItem(safetyAcknowledgementKey, "true");
    setShowSafetyModal(false);
  }, []);

  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand-button" onClick={() => navigate("/start")} aria-label="Go to start">
          <HeartPulse size={22} aria-hidden />
          <span>
            <strong>Cardiac Analgesia</strong>
          </span>
        </button>
      </header>

      <main className="screen">
        <div key={`${route.path}-${route.id}`} className="page-frame">
          {showSafetyModal && <SafetyModal onAcknowledge={acknowledgeSafety} />}
          {route.id === "start" && <StartScreen navigate={navigate} />}
          {route.id === "setup" && <SetupScreen state={state} updateState={updateState} navigate={navigate} />}
          {route.id === "tools" && <ToolsOverview state={state} navigate={navigate} />}
          {route.group === "workflow" && <WorkflowScreen route={route} state={state} navigate={navigate} updateState={updateState} />}
          {route.id === "pain-score" && <PainScoreTool state={state} updateState={updateState} navigate={navigate} />}
          {route.id === "function" && <FunctionTool state={state} updateState={updateState} navigate={navigate} />}
          {route.id === "cpot" && <CpotTool state={state} updateState={updateState} navigate={navigate} />}
        </div>
      </main>

      <nav className="bottom-nav" aria-label="Workflow navigation">
        <button className={route.id === "setup" ? "active" : ""} onClick={() => navigate("/setup")}>
          <ClipboardList size={18} aria-hidden />
          <span>Details</span>
        </button>
        <button className={route.group === "workflow" ? "active" : ""} onClick={() => navigate(pathForStage(state.stage))}>
          <Stethoscope size={18} aria-hidden />
          <span>Pathway</span>
        </button>
        <button className={route.group === "tool" ? "active" : ""} onClick={() => navigate("/tools")}>
          <Activity size={18} aria-hidden />
          <span>Tools</span>
        </button>
      </nav>
    </div>
  );
}

function SafetyModal({ onAcknowledge }: { onAcknowledge: () => void }) {
  return (
    <div className="modal-backdrop" role="presentation">
      <section className="safety-modal" role="dialog" aria-modal="true" aria-labelledby="safety-title">
        <AlertTriangle size={24} aria-hidden />
        <div>
          <h2 id="safety-title">Before using this guide</h2>
          <p>
            Use this alongside the approved local guideline and clinician judgement. Do not enter patient names, identifiers, dates of birth,
            or free-text clinical notes.
          </p>
        </div>
        <button className="icon-text-button primary" onClick={onAcknowledge}>
          <ArrowRight size={18} aria-hidden />
          <span>I understand</span>
        </button>
      </section>
    </div>
  );
}

function StartScreen({ navigate }: { navigate: (path: string, patch?: NavPatch) => void }) {
  return (
    <section className="stack">
      <div className="page-heading">
        <h1>Multimodal peri-operative analgesia for routine cardiac surgery</h1>
      </div>

      <div className="action-grid">
        <button className="primary-action" onClick={() => navigate("/setup")}>
          <ClipboardList size={22} aria-hidden />
          <span>Customise patient details</span>
          <ArrowRight size={18} aria-hidden />
        </button>
        {workflowNav.map((route) => (
          <button key={route.id} className="route-tile" onClick={() => navigate(route.path, { stage: route.id })}>
            <span>{route.label}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function SetupScreen({
  state,
  updateState,
  navigate,
}: {
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  navigate: (path: string, patch?: NavPatch) => void;
}) {
  return (
    <section className="stack">
      <div className="page-heading compact">
        <h1>Patient details</h1>
      </div>

      <Fieldset legend="Age band" info={patientDetailControlHelp.age}>
        <SegmentedControl
          value={state.age}
          options={[
            ["unknown", "Unknown"],
            ["under75", "< 75"],
            ["over75", "75+"],
          ]}
          onChange={(age) => updateState({ age: age as WorkflowState["age"] })}
        />
      </Fieldset>

      <Fieldset legend="eGFR band" info={patientDetailControlHelp.egfr}>
        <SegmentedControl
          value={state.egfr}
          options={[
            ["unknown", "Unknown"],
            ["gt40", "> 40"],
            ["30to40", "30-40"],
            ["20to30", "20-30"],
            ["lt20", "< 20 / ESRF"],
          ]}
          onChange={(egfr) => updateState({ egfr: egfr as EgfrBand })}
        />
      </Fieldset>

      <Fieldset legend="Clinical features" info="These factors narrow recommendations where the pathway depends on pain risk, contraindications, route tolerance, functional limitation, renal cautions, or medicine-specific exclusions.">
        <FlagResponseGrid flags={clinicalFlags} state={state} updateState={updateState} />
      </Fieldset>

      <Fieldset legend="Allergies and intolerances" info={patientDetailHelpText({ kind: "allergies", flags: allergyFlags })}>
        <AllergyResponsePanel flags={allergyFlags} state={state} updateState={updateState} />
      </Fieldset>

      <div className="action-row">
        <button className="icon-text-button primary" onClick={() => navigate(pathForStage(state.stage), { stage: state.stage })}>
          <ArrowRight size={18} aria-hidden />
          <span>Continue</span>
        </button>
      </div>
    </section>
  );
}

function WorkflowScreen({
  route,
  state,
  navigate,
  updateState,
}: {
  route: WorkflowRoute;
  state: WorkflowState;
  navigate: (path: string, patch?: NavPatch) => void;
  updateState: (patch: NavPatch) => void;
}) {
  const groups = groupRecommendations(route.id, state);
  const targetGroups = groups.filter((group) => group.kind === "target");
  const interventionBlocks = buildInterventionBlocks(groups.filter((group) => group.kind === "intervention"));
  const choiceGroups = useMemo(() => choiceGroupsForRoute(route.id), [route.id]);

  useEffect(() => {
    if (route.group === "workflow" && state.stage !== route.id) {
      updateState({ stage: route.id as WorkflowState["stage"] });
    }
  }, [route.group, route.id, state.stage, updateState]);

  return (
    <section className="stack">
      <div className="route-strip" aria-label="Workflow steps">
        {workflowNav.map((navRoute) => (
          <button
            key={navRoute.id}
            className={navRoute.id === route.id ? "active" : ""}
            onClick={() => navigate(navRoute.path, { stage: navRoute.id as WorkflowState["stage"] })}
          >
            {navRoute.shortLabel}
          </button>
        ))}
      </div>

      <div className="page-heading compact">
        <h1>{route.label}</h1>
      </div>

      <PagePatientDetailsPanel routeId={route.id} state={state} updateState={updateState} />

      {targetGroups.length > 0 && (
        <section className="recommendation-section" aria-label={`${route.label} targets`}>
          <h2>Targets</h2>
          <div className="recommendation-list">
            {targetGroups.map((group) => (
              <RecommendationGroupCard
                key={group.id}
                group={group}
                state={state}
                updateState={updateState}
                choiceGroups={choiceGroups}
                navigate={navigate}
              />
            ))}
          </div>
        </section>
      )}

      <section className="recommendation-section" aria-label={`${route.label} interventions`}>
        <h2>Interventions</h2>
        <div className="recommendation-list">
          {interventionBlocks.map((block) => (
            <InterventionBlockView
              key={block.id}
              block={block}
              state={state}
              updateState={updateState}
              choiceGroups={choiceGroups}
              navigate={navigate}
            />
          ))}
        </div>
      </section>
    </section>
  );
}

function InterventionBlockView({
  block,
  state,
  updateState,
  choiceGroups,
  navigate,
}: {
  block: InterventionBlock;
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  choiceGroups: Map<string, OpioidChoice[]>;
  navigate: (path: string, patch?: NavPatch) => void;
}) {
  if (!block.choiceGroup) {
    return (
      <RecommendationGroupCard
        group={block.groups[0]}
        state={state}
        updateState={updateState}
        choiceGroups={choiceGroups}
        navigate={navigate}
      />
    );
  }

  const values = choiceGroups.get(block.choiceGroup) ?? [];
  const hasOpioidSelection = state.opioid !== "unknown" && values.includes(state.opioid);
  const hasGenericSelection = state.choices.some((choice) => choice.startsWith(`${block.choiceGroup}:`));
  const hasSelection = hasOpioidSelection || hasGenericSelection;
  const hasSelectableOptions = block.groups.some((group) => group.choiceValue !== undefined || group.optionValue !== undefined);

  return (
    <section className="choice-block" aria-label="Choose one option">
      {hasSelectableOptions ? (
        <p className="choice-instruction">{hasSelection ? "One option selected" : "Pick one option"}</p>
      ) : (
        <p className="choice-instruction">Either-or options</p>
      )}
      {block.groups.map((group, index) => (
        <div key={group.id} className="choice-option-shell">
          {index > 0 && <div className="or-divider">or</div>}
          <RecommendationGroupCard
            group={group}
            state={state}
            updateState={updateState}
            choiceGroups={choiceGroups}
            navigate={navigate}
            forceUnpicked={hasSelectableOptions && hasSelection === false}
          />
        </div>
      ))}
    </section>
  );
}

function RecommendationGroupCard({
  group,
  state,
  updateState,
  choiceGroups,
  navigate,
  forceUnpicked = false,
}: {
  group: RecommendationGroup;
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  choiceGroups: Map<string, OpioidChoice[]>;
  navigate: (path: string, patch?: NavPatch) => void;
  forceUnpicked?: boolean;
}) {
  const selectedChoice = group.choiceValue !== undefined && state.opioid === group.choiceValue;
  const selectedOption =
    group.choiceGroup !== undefined &&
    group.optionValue !== undefined &&
    state.choices.includes(choiceToken(group.choiceGroup, group.optionValue));
  const isOtherChoice =
    group.choiceGroup !== undefined &&
    group.choiceValue !== undefined &&
    state.opioid !== "unknown" &&
    state.opioid !== group.choiceValue &&
    (choiceGroups.get(group.choiceGroup) ?? []).includes(state.opioid);
  const isOtherGenericChoice =
    group.choiceGroup !== undefined &&
    group.optionValue !== undefined &&
    state.choices.some((choice) => choice.startsWith(`${group.choiceGroup}:`) && choice !== choiceToken(group.choiceGroup!, group.optionValue!));
  const nextStep = nextStepForTarget(group.id);
  const className = [
    "recommendation-card",
    group.strength,
    group.kind === "target" ? "target-card" : "",
    selectedChoice || selectedOption ? "selected-choice" : "",
    isOtherChoice || isOtherGenericChoice ? "deemphasised-choice" : "",
    forceUnpicked ? "unpicked-choice" : "",
    group.isNotApplicable ? "not-applicable" : "",
    group.blockedReasons.length > 0 ? "allergy-blocked" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <article className={className}>
      <div className="card-heading">
        <span className="strength-dot" aria-hidden />
        <div className="intervention-title">
          <p className="card-kicker">{group.kind === "target" ? "Target" : strengthLabel(group.strength)}</p>
          <h3>{group.title}</h3>
        </div>
        {(group.choiceValue || group.optionValue) && (
          <button
            className={selectedChoice || selectedOption ? "choice-pill selected" : "choice-pill"}
            type="button"
            aria-label={selectedChoice || selectedOption ? `${group.title} selected` : `Select ${group.title}`}
            disabled={group.blockedReasons.length > 0 || group.isNotApplicable}
            onClick={() => selectChoice(group, state, updateState)}
          >
            {selectedChoice || selectedOption ? "Selected" : "Select"}
          </button>
        )}
      </div>

      {group.missingContext.length > 0 && (
        <p className="detail-warning">Set {formatList(group.missingContext)} above to narrow this intervention.</p>
      )}
      {group.blockedReasons.length > 0 && (
        <div className="allergy-block">
          <AlertTriangle size={16} aria-hidden />
          <p>Do not use: {formatList(group.blockedReasons)} recorded in allergies.</p>
        </div>
      )}

      {group.kind === "target" ? (
        <TargetItems items={group.items.flatMap((recommendation) => [recommendation.summary, ...recommendation.details])} navigate={navigate} state={state} />
      ) : (
        <div className="variant-list">
          {group.items.map((recommendation) => (
            <section key={recommendation.id} className="variant-item">
              {group.items.length > 1 && <h4>{recommendation.title}</h4>}
              <p className="summary">
                <TextWithToolLinks text={recommendation.summary} navigate={navigate} state={state} />
              </p>
              <DetailLines
                details={[
                  ...recommendation.details,
                  ...(group.choiceGroup === "handover-opioid" && state.flags.includes("bmiAbove30")
                    ? ["For BMI above 30, consider adjusted weight for dosing."]
                    : []),
                  ...(group.choiceGroup === "handover-opioid" && !state.flags.includes("bmiAbove30") && !state.noFlags.includes("bmiAbove30")
                    ? ["If BMI is above 30, consider adjusted weight for dosing."]
                    : []),
                ]}
                navigate={navigate}
                state={state}
              />
              {recommendation.cautions && (
                <div className="caution-block">
                  <AlertTriangle size={16} aria-hidden />
                  <p>
                    <TextWithToolLinks text={recommendation.cautions.join(" ")} navigate={navigate} state={state} />
                  </p>
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {group.isNotApplicable && group.blockedReasons.length === 0 && (
        <p className="not-applicable-text">
          Not applicable for the selected patient details{group.notApplicableReason ? `: ${group.notApplicableReason}` : "."}
        </p>
      )}

      {nextStep && (
        <div className="target-action-row">
          <button
            className="icon-text-button primary"
            type="button"
            onClick={() => navigate(nextStep.path, { stage: nextStep.id as WorkflowState["stage"] })}
          >
            <ArrowRight size={18} aria-hidden />
            <span>Next phase: {nextStep.label}</span>
          </button>
        </div>
      )}
    </article>
  );
}

function TargetItems({ items, navigate, state }: { items: string[]; navigate: (path: string, patch?: NavPatch) => void; state: WorkflowState }) {
  if (items.length === 0) return null;
  if (items.length === 1) {
    return (
      <p className="target-prose">
        <TextWithToolLinks text={items[0]} navigate={navigate} state={state} />
      </p>
    );
  }

  return (
    <div className="target-list">
      {items.map((target) => (
        <div key={target} className="target-list-item">
          <CheckCircle2 size={18} aria-hidden />
          <p>
            <TextWithToolLinks text={target} navigate={navigate} state={state} />
          </p>
        </div>
      ))}
    </div>
  );
}

function DetailLines({ details, navigate, state }: { details: string[]; navigate: (path: string, patch?: NavPatch) => void; state: WorkflowState }) {
  if (details.length === 0) return null;
  if (details.length === 1) {
    return (
      <p className="detail-prose">
        <TextWithToolLinks text={details[0]} navigate={navigate} state={state} />
      </p>
    );
  }

  return (
    <ul>
      {details.map((detail) => (
        <li key={detail}>
          <TextWithToolLinks text={detail} navigate={navigate} state={state} />
        </li>
      ))}
    </ul>
  );
}

function PagePatientDetailsPanel({
  routeId,
  state,
  updateState,
}: {
  routeId: WorkflowRouteId;
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
}) {
  const controls = controlsForRoute(routeId);
  const allDetailsSet = arePatientDetailsComplete(controls, state);
  const [isExpanded, setIsExpanded] = useState(() => (allDetailsSet ? false : readPatientDetailsExpanded(routeId, true)));

  useEffect(() => {
    setIsExpanded(allDetailsSet ? false : readPatientDetailsExpanded(routeId, true));
  }, [routeId]);

  const setPersistedExpanded = useCallback(
    (expanded: boolean) => {
      localStorage.setItem(`${patientDetailsExpandedStoragePrefix}${routeId}`, expanded ? "true" : "false");
      setIsExpanded(expanded);
    },
    [routeId],
  );

  if (controls.controls.length === 0 && controls.flags.length === 0 && controls.allergies.length === 0) {
    return null;
  }

  return (
    <section className="patient-details-panel" aria-label="Patient details for this phase">
      <div className="patient-details-header">
        <div>
          <h2 className="patient-details-title">Patient details</h2>
          <p className={allDetailsSet ? "details-status complete" : "details-status incomplete"}>
            {allDetailsSet ? "Complete for this page" : "Incomplete for this page"}
          </p>
        </div>
        <div className="patient-details-actions">
          {isExpanded && (
            <button className="icon-text-button secondary" type="button" onClick={() => clearPatientDetails(controls, state, updateState)}>
              Clear details
            </button>
          )}
          <button className="icon-text-button secondary" type="button" onClick={() => setPersistedExpanded(!isExpanded)}>
            {isExpanded ? "Hide" : "Edit"}
          </button>
        </div>
      </div>

      <div className={isExpanded ? "patient-details-body expanded" : "patient-details-body collapsed"} aria-hidden={!isExpanded}>
        {controls.controls.includes("age") && (
          <DetailControl label="Age band" info={affectedInterventionsText(routeId, { kind: "control", control: "age" })}>
            <SegmentedControl
              value={state.age}
              options={[
                ["unknown", "Unknown"],
                ["under75", "< 75"],
                ["over75", "75+"],
              ]}
              onChange={(age) => updateState({ age: age as WorkflowState["age"] })}
            />
          </DetailControl>
        )}

        {controls.controls.includes("egfr") && (
          <DetailControl label="eGFR band" info={affectedInterventionsText(routeId, { kind: "control", control: "egfr" })}>
            <SegmentedControl
              value={state.egfr}
              options={[
                ["unknown", "Unknown"],
                ["gt40", "> 40"],
                ["30to40", "30-40"],
                ["20to30", "20-30"],
                ["lt20", "< 20 / ESRF"],
              ]}
              onChange={(egfr) => updateState({ egfr: egfr as EgfrBand })}
            />
          </DetailControl>
        )}

        {controls.controls.includes("sedation") && (
          <DetailControl label="Patient state" info={affectedInterventionsText(routeId, { kind: "control", control: "sedation" })}>
            <SegmentedControl
              value={state.sedation}
              options={[
                ["unknown", "Unknown"],
                ["sedated", "Sedated"],
                ["extubated", "Extubated"],
              ]}
              onChange={(sedation) => updateState({ sedation: sedation as WorkflowState["sedation"] })}
            />
          </DetailControl>
        )}

        {controls.controls.includes("opioid") && (
          <DetailControl
            label={routeId === "handover" ? "Post-operative IV opioid loading option" : "Post-operative IV opioid option"}
            info={affectedInterventionsText(routeId, { kind: "control", control: "opioid" })}
          >
            <SegmentedControl
              value={state.opioid}
              options={opioidOptionsForRoute(routeId)}
              onChange={(opioid) => updateState({ opioid: opioid as OpioidChoice })}
            />
          </DetailControl>
        )}

        {controls.controls.includes("pain") && (
          <DetailControl label="Pain score" info={affectedInterventionsText(routeId, { kind: "control", control: "pain" })}>
            <ScoreButtons max={10} value={state.pain} onChange={(pain) => updateState({ pain })} />
          </DetailControl>
        )}

        {controls.controls.includes("cpot") && (
          <DetailControl label={`CPOT: ${state.cpot ?? "not set"}`} info={affectedInterventionsText(routeId, { kind: "control", control: "cpot" })}>
            <SegmentedControl
              value={state.cpot === null ? "unknown" : String(state.cpot)}
              options={[
                ["unknown", "Unknown"],
                ["0", "0"],
                ["1", "1"],
                ["2", "2"],
                ["3", "3+"],
              ]}
              onChange={(cpot) => updateState({ cpot: cpot === "unknown" ? null : Number(cpot) })}
            />
          </DetailControl>
        )}

        {controls.flags.map((flag) => (
          <DetailControl key={flag} label={flagLabels[flag]} info={affectedInterventionsText(routeId, { kind: "flag", flag })}>
            <SegmentedControl
              value={flagAnswer(flag, state)}
              options={[
                ["unknown", "Unknown"],
                ["yes", "Yes"],
                ["no", "No"],
              ]}
              onChange={(value) => updateFlagAnswer(flag, value as "unknown" | "yes" | "no", state, updateState)}
            />
          </DetailControl>
        ))}

        {controls.allergies.length > 0 && (
          <DetailControl label="Allergies and intolerances" info={affectedInterventionsText(routeId, { kind: "allergies", flags: controls.allergies })}>
            <AllergyResponsePanel flags={controls.allergies} state={state} updateState={updateState} />
          </DetailControl>
        )}
      </div>
    </section>
  );
}

function readPatientDetailsExpanded(routeId: WorkflowRouteId, fallback: boolean) {
  const stored = localStorage.getItem(`${patientDetailsExpandedStoragePrefix}${routeId}`);
  if (stored === "true") return true;
  if (stored === "false") return false;
  return fallback;
}

function DetailControl({ label, info, children }: { label: string; info?: string; children: React.ReactNode }) {
  return (
    <div className="detail-control">
      <div className="detail-label-row">
        <span>{label}</span>
        {info && <InfoTip text={info} />}
      </div>
      {children}
    </div>
  );
}

function InfoTip({ text }: { text: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <span className="info-tip">
      <button
        type="button"
        aria-label="What this affects"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((current) => !current)}
        onBlur={() => setIsOpen(false)}
      >
        <Info size={15} aria-hidden />
      </button>
      <span className={isOpen ? "info-bubble open" : "info-bubble"} role="tooltip">
        {text}
      </span>
    </span>
  );
}

function clearPatientDetails(
  controls: { controls: PatientDetailControl[]; flags: WorkflowFlag[]; allergies: WorkflowFlag[] },
  state: WorkflowState,
  updateState: (patch: NavPatch) => void,
) {
  const patch: NavPatch = {
    flags: state.flags.filter((flag) => !controls.flags.includes(flag) && !controls.allergies.includes(flag)),
    noFlags: state.noFlags.filter((flag) => !controls.flags.includes(flag) && !controls.allergies.includes(flag)),
  };

  if (controls.controls.includes("age")) patch.age = "unknown";
  if (controls.controls.includes("egfr")) patch.egfr = "unknown";
  if (controls.controls.includes("sedation")) patch.sedation = "unknown";
  if (controls.controls.includes("opioid")) patch.opioid = "unknown";
  if (controls.controls.includes("pain")) patch.pain = null;
  if (controls.controls.includes("cpot")) patch.cpot = null;
  if (controls.allergies.length > 0) patch.allergies = "unknown";

  updateState(patch);
}

function FlagResponseGrid({
  flags,
  state,
  updateState,
  infoForFlag,
}: {
  flags: WorkflowFlag[];
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  infoForFlag?: (flag: WorkflowFlag) => string;
}) {
  return (
    <div className="tri-state-grid">
      {flags.map((flag) => (
        <div key={flag} className="tri-state-row">
          <span className="tri-state-label">
            <span>{flagLabels[flag]}</span>
            {infoForFlag && <InfoTip text={infoForFlag(flag)} />}
          </span>
          <SegmentedControl
            value={flagAnswer(flag, state)}
            options={[
              ["unknown", "Unknown"],
              ["yes", "Yes"],
              ["no", "No"],
            ]}
            onChange={(value) => updateFlagAnswer(flag, value as "unknown" | "yes" | "no", state, updateState)}
          />
        </div>
      ))}
    </div>
  );
}

function AllergyResponsePanel({
  flags,
  state,
  updateState,
}: {
  flags: WorkflowFlag[];
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
}) {
  return (
    <div className="allergy-panel">
      <SegmentedControl
        value={state.allergies}
        options={[
          ["unknown", "Unknown"],
          ["yes", "Yes"],
          ["no", "No"],
        ]}
        onChange={(value) => updateAllergyStatus(value as AllergyStatus, flags, state, updateState)}
      />
      {state.allergies === "yes" && <FlagResponseGrid flags={flags} state={state} updateState={updateState} />}
    </div>
  );
}

function updateAllergyStatus(
  allergies: AllergyStatus,
  flags: WorkflowFlag[],
  state: WorkflowState,
  updateState: (patch: NavPatch) => void,
) {
  if (allergies === "no") {
    updateState({
      allergies,
      flags: state.flags.filter((flag) => !flags.includes(flag)),
      noFlags: Array.from(new Set([...state.noFlags, ...flags])),
      opioid: flags.some((flag) => allergyToOpioid[flag] === state.opioid) ? "unknown" : state.opioid,
    });
    return;
  }

  updateState({ allergies });
}

function ScoreButtons({ max, value, onChange }: { max: number; value: number | null; onChange: (value: number | null) => void }) {
  return (
    <div className="score-button-grid">
      <button className={value === null ? "selected" : ""} type="button" onClick={() => onChange(null)}>
        U/K
      </button>
      {Array.from({ length: max + 1 }, (_, score) => (
        <button key={score} className={value === score ? "selected" : ""} type="button" onClick={() => onChange(value === score ? null : score)}>
          {score}
        </button>
      ))}
    </div>
  );
}

function flagAnswer(flag: WorkflowFlag, state: WorkflowState) {
  if (state.flags.includes(flag)) return "yes";
  if (state.noFlags.includes(flag)) return "no";
  return "unknown";
}

function updateFlagAnswer(
  flag: WorkflowFlag,
  answer: "unknown" | "yes" | "no",
  state: WorkflowState,
  updateState: (patch: NavPatch) => void,
) {
  updateState({
    flags: answer === "yes" ? [...state.flags.filter((item) => item !== flag), flag] : state.flags.filter((item) => item !== flag),
    noFlags: answer === "no" ? [...state.noFlags.filter((item) => item !== flag), flag] : state.noFlags.filter((item) => item !== flag),
    allergies: allergyFlags.includes(flag) && answer === "yes" ? "yes" : state.allergies,
    opioid: answer === "yes" && allergyToOpioid[flag] === state.opioid ? "unknown" : state.opioid,
  });
}

function TextWithToolLinks({
  text,
  navigate,
  state,
}: {
  text: string;
  navigate: (path: string, patch?: NavPatch) => void;
  state: WorkflowState;
}) {
  const matches: Array<{ pattern: RegExp; path: string }> = [
    { pattern: /\bCPOT\b/g, path: "/tools/cpot" },
    { pattern: /\bNPS\b|\bpain score(s)?\b/gi, path: "/tools/pain-score" },
    { pattern: /\bfunctional limitation(s)?\b|\bfunction\b/gi, path: "/tools/function" },
  ];
  const segments: Array<{ text: string; path?: string }> = [{ text }];

  for (const match of matches) {
    for (let index = 0; index < segments.length; index += 1) {
      const segment = segments[index];
      if (segment.path) continue;
      const parts: Array<{ text: string; path?: string }> = [];
      let lastIndex = 0;
      for (const result of segment.text.matchAll(match.pattern)) {
        if (result.index === undefined) continue;
        if (result.index > lastIndex) {
          parts.push({ text: segment.text.slice(lastIndex, result.index) });
        }
        parts.push({ text: result[0], path: match.path });
        lastIndex = result.index + result[0].length;
      }
      if (parts.length > 0) {
        if (lastIndex < segment.text.length) {
          parts.push({ text: segment.text.slice(lastIndex) });
        }
        segments.splice(index, 1, ...parts);
        index += parts.length - 1;
      }
    }
  }

  return (
    <>
      {segments.map((segment, index) =>
        segment.path ? (
          <button key={`${segment.text}-${index}`} className="inline-link" type="button" onClick={() => navigate(segment.path!, state)}>
            {segment.text}
          </button>
        ) : (
          <span key={`${segment.text}-${index}`}>{segment.text}</span>
        ),
      )}
    </>
  );
}

function controlsForRoute(routeId: WorkflowRouteId): { controls: PatientDetailControl[]; flags: WorkflowFlag[]; allergies: WorkflowFlag[] } {
  const routeRecommendations = recommendations.filter((recommendation) => recommendation.routeIds.includes(routeId));
  const controls = new Set<PatientDetailControl>();
  const flags = new Set<WorkflowFlag>();
  const allergies = new Set<WorkflowFlag>();

  for (const recommendation of routeRecommendations) {
    for (const control of controlsForCriteria(recommendation.criteria)) {
      controls.add(control);
    }
    for (const flag of flagsForCriteria(recommendation.criteria)) {
      flags.add(flag as WorkflowFlag);
    }
    for (const flag of recommendation.blockedByFlags ?? []) {
      allergies.add(flag);
    }
  }

  if (routeId === "handover") {
    flags.add("bmiAbove30");
  }

  return {
    controls: Array.from(controls),
    flags: Array.from(flags).filter((flag) => !allergyFlags.includes(flag)),
    allergies: Array.from(allergies),
  };
}

function controlsForCriteria(criteria: Recommendation["criteria"]): PatientDetailControl[] {
  if (!criteria) return [];
  return [
    ...(criteria.any ?? []).flatMap((branch) => controlsForCriteria(branch)),
    ...(criteria.age ? (["age"] as const) : []),
    ...(criteria.egfr ? (["egfr"] as const) : []),
    ...(criteria.sedation ? (["sedation"] as const) : []),
    ...(criteria.opioid ? (["opioid"] as const) : []),
    ...(criteria.painAtLeast !== undefined ? (["pain"] as const) : []),
    ...(criteria.cpotAtLeast !== undefined ? (["cpot"] as const) : []),
  ];
}

function flagsForCriteria(criteria: Recommendation["criteria"]): string[] {
  if (!criteria) return [];
  return [...(criteria.flagsAny ?? []), ...(criteria.any ?? []).flatMap((branch) => flagsForCriteria(branch))];
}

type PatientDetailInfoSubject =
  | { kind: "control"; control: PatientDetailControl }
  | { kind: "flag"; flag: WorkflowFlag }
  | { kind: "allergies"; flags: WorkflowFlag[] };

const patientDetailControlHelp: Record<PatientDetailControl, string> = {
  age: "Age changes eligibility for gabapentinoids. Pregabalin is not indicated at 75 years or older; below 75 years, the dose is narrowed with eGFR.",
  egfr: "Renal function changes pregabalin eligibility, post-operative IV opioid loading, oral analgesia choices, and buprenorphine use in severe renal impairment.",
  sedation: "Patient state separates sedated ITU care from extubated care, including whether CPOT or verbal pain scoring is more relevant.",
  opioid: "This is the selected post-operative IV opioid loading option. Later IV bolus advice is aligned with the opioid already used.",
  pain: "Pain score changes escalation advice. NPS above 4, or moderate to severe pain, brings breakthrough and PCA recommendations into view.",
  cpot: "CPOT is used when verbal pain scoring is not available. CPOT above 2 brings breakthrough analgesia and escalation advice into view.",
};

const patientDetailFlagHelp: Partial<Record<WorkflowFlag, string>> = {
  chronicPain: "Marks higher risk of challenging or persistent pain and opens gabapentinoid or ketamine considerations where relevant.",
  chronicGabapentinoid: "Supports pre-operative gabapentinoid consideration when renal function and age allow it.",
  polyneuropathy: "Supports pre-operative gabapentinoid consideration when renal function and age allow it.",
  opioidUse: "Marks opioid tolerance or higher pain risk and opens ketamine or opioid-sparing adjunct considerations.",
  persistentPostOpPainRisk: "Highlights patients at risk of persistent post-operative pain, supporting remifentanil caution and opioid-sparing strategies.",
  neuropathicPain: "Supports post-operative gabapentinoid escalation when pain has neuropathic features.",
  methadone: "Marks higher-risk analgesia needs and supports ketamine consideration.",
  ivdu: "Marks higher-risk analgesia needs and supports ketamine consideration.",
  mentalHealth: "Anxiety, depression, PTSD, or pain catastrophising increase persistent pain risk and support ketamine or remifentanil caution where relevant.",
  oralNotTolerated: "Triggers non-oral escalation options when oral analgesia cannot be used.",
  functionalLimitation: "Triggers escalation when pain limits deep breathing, coughing, getting out of bed, or mobilising.",
  bmiAbove30: "Adds adjusted-weight dosing advice where weight-based opioid loading is shown.",
  weightAbove50: "Changes oral paracetamol advice because the listed four-times-daily dose applies when weight is above 50 kg.",
  ischaemicHeartDisease: "Marks a listed NSAID contraindication and changes NSAID advice to avoid use.",
  congestiveHeartFailure: "Marks a listed NSAID contraindication and changes NSAID advice to avoid use.",
  cerebrovascularDisease: "Marks a listed NSAID contraindication and changes NSAID advice to avoid use.",
  peripheralVascularDisease: "Marks a listed NSAID contraindication and changes NSAID advice to avoid use.",
  renalImpairment: "Marks a listed NSAID contraindication and changes NSAID advice to avoid use.",
  giBleedingHistory: "Marks a listed NSAID contraindication and changes NSAID advice to avoid use.",
  coagulationDefect: "Marks a listed NSAID contraindication and changes NSAID advice to avoid use.",
  uncontrolledHypertension: "Marks a listed NSAID contraindication and changes NSAID advice to avoid use.",
  highDegreeHeartBlock: "Blocks dexmedetomidine use where the guideline warns to avoid it.",
  severeBradycardia: "Blocks dexmedetomidine use where the guideline warns to avoid it.",
  hypotensionNoradrenaline: "Blocks dexmedetomidine use when hypotension requires noradrenaline above 0.1 mcg/kg/min.",
};

function affectedInterventionsText(routeId: WorkflowRouteId, subject: PatientDetailInfoSubject) {
  const names = affectedInterventions(routeId, subject);
  const helpText = patientDetailHelpText(subject);
  if (names.length === 0) {
    return `${helpText} No interventions on this tab are changed by this detail.`;
  }

  return helpText;
}

function patientDetailHelpText(subject: PatientDetailInfoSubject) {
  if (subject.kind === "control") {
    return patientDetailControlHelp[subject.control];
  }

  if (subject.kind === "allergies") {
    return "Set this to Yes to record specific allergies or intolerances. A marked allergy blocks the affected intervention and clears a conflicting selected opioid option.";
  }

  return patientDetailFlagHelp[subject.flag] ?? "This patient factor narrows the recommendations shown on this page.";
}

function affectedInterventions(routeId: WorkflowRouteId, subject: PatientDetailInfoSubject) {
  const routeRecommendations = recommendations.filter((recommendation) => recommendation.routeIds.includes(routeId));
  const affected = routeRecommendations.filter((recommendation) => recommendation.cardKind !== "target" && isAffectedBySubject(recommendation, routeId, subject));

  return Array.from(new Set(affected.map((recommendation) => recommendation.cardTitle ?? recommendation.title))).sort((a, b) => a.localeCompare(b));
}

function clinicalFeatureInfo(flag: WorkflowFlag) {
  const affected = recommendations.filter(
    (recommendation) => recommendation.cardKind !== "target" && flagsForCriteria(recommendation.criteria).includes(flag),
  );
  const names = Array.from(new Set(affected.map((recommendation) => recommendation.cardTitle ?? recommendation.title))).sort((a, b) => a.localeCompare(b));
  const base = patientDetailFlagHelp[flag] ?? "This patient factor narrows recommendations in the pathway.";

  if (names.length === 0) {
    return base;
  }

  return `${base} Changes: ${formatList(names)}.`;
}

function isAffectedBySubject(recommendation: Recommendation, routeId: WorkflowRouteId, subject: PatientDetailInfoSubject) {
  if (subject.kind === "allergies") {
    return (recommendation.blockedByFlags ?? []).some((flag) => subject.flags.includes(flag));
  }

  if (subject.kind === "flag") {
    if (flagsForCriteria(recommendation.criteria).includes(subject.flag)) return true;
    return subject.flag === "bmiAbove30" && routeId === "handover" && recommendation.choiceGroup === "handover-opioid";
  }

  if (controlsForCriteria(recommendation.criteria).includes(subject.control)) return true;
  if (subject.control === "opioid") return recommendation.choiceValue !== undefined && routeId !== "during-surgery";
  return false;
}

function opioidOptionsForRoute(routeId: WorkflowRouteId): Array<[string, string]> {
  if (routeId === "during-surgery") {
    return [
      ["unknown", "Not selected"],
      ["fentanyl", "Fentanyl"],
      ["alfentanil", "Alfentanil"],
      ["remifentanil", "Remifentanil"],
    ];
  }

  return [
    ["unknown", "Not selected"],
    ["morphine", "Morphine"],
    ["oxycodone", "Oxycodone"],
    ["fentanyl", "Fentanyl"],
  ];
}

function choiceGroupsForRoute(routeId: WorkflowRouteId) {
  const choiceGroups = new Map<string, OpioidChoice[]>();
  for (const recommendation of recommendations.filter((item) => item.routeIds.includes(routeId))) {
    if (!recommendation.choiceGroup || !recommendation.choiceValue) continue;
    const current = choiceGroups.get(recommendation.choiceGroup) ?? [];
    if (!current.includes(recommendation.choiceValue)) {
      choiceGroups.set(recommendation.choiceGroup, [...current, recommendation.choiceValue]);
    }
  }
  return choiceGroups;
}

function groupRecommendations(routeId: WorkflowRouteId, state: WorkflowState): RecommendationGroup[] {
  const routeRecommendations = recommendations.filter((recommendation) => recommendation.routeIds.includes(routeId));
  const groups = new Map<string, Recommendation[]>();

  for (const recommendation of routeRecommendations) {
    const key = recommendation.cardId ?? recommendation.id;
    groups.set(key, [...(groups.get(key) ?? []), recommendation]);
  }

  return Array.from(groups.entries()).map(([id, items]) => {
    const evaluated = items.map((recommendation) => {
      const result = criteriaMatches(recommendation.criteria, state);
      const blockedReasons = (recommendation.blockedByFlags ?? [])
        .filter((flag) => state.flags.includes(flag))
        .map((flag) => flagLabels[flag]);
      return {
        ...recommendation,
        isContextualMatch: result.isMatch,
        missingContext: result.missingContext,
        hasHardMismatch: result.hasHardMismatch,
        blockedReasons,
      };
    });
    const unblocked = evaluated.filter((item) => item.blockedReasons.length === 0);
    const matching = unblocked.filter((item) => item.isContextualMatch);
    const needsDetails = unblocked.filter((item) => item.missingContext.length > 0 && !item.hasHardMismatch);
    const displayed = matching.length > 0 ? matching : needsDetails;
    const basis = displayed.length > 0 ? displayed : evaluated;
    const strongest = basis.reduce<RecommendationStrength>(
      (current, item) => (strengthOrder[item.strength] < strengthOrder[current] ? item.strength : current),
      basis[0]?.strength ?? "recommended",
    );
    const missingContext = Array.from(new Set(displayed.flatMap((item) => item.missingContext)));
    const first = items[0];
    const blockedReasons = Array.from(new Set(evaluated.flatMap((item) => item.blockedReasons)));

    return {
      id,
      title: first.cardTitle ?? first.title,
      kind: first.cardKind ?? "intervention",
      strength: strongest,
      choiceGroup: first.choiceGroup,
      choiceValue: first.choiceValue,
      optionValue: first.optionValue,
      items: displayed,
      missingContext,
      isNotApplicable: displayed.length === 0,
      notApplicableReason: displayed.length === 0 ? notApplicableReason(evaluated, state) : "",
      blockedReasons: displayed.length === 0 ? blockedReasons : [],
    };
  }).sort((a, b) => Number(a.isNotApplicable) - Number(b.isNotApplicable) || strengthOrder[a.strength] - strengthOrder[b.strength]);
}

function notApplicableReason(items: Array<Recommendation & { hasHardMismatch: boolean }>, state: WorkflowState) {
  const criteriaText = Array.from(new Set(items.flatMap((item) => criteriaRequirementText(item.criteria))));
  if (criteriaText.length > 0) {
    return `requires ${formatList(criteriaText)}`;
  }

  const caution = items.find((item) => item.cautions && item.cautions.length > 0)?.cautions?.[0];
  if (caution) return caution;

  if (state.age !== "unknown" || state.egfr !== "unknown" || state.opioid !== "unknown") {
    return "the selected details do not match this option";
  }

  return "";
}

function criteriaRequirementText(criteria: Recommendation["criteria"]): string[] {
  if (!criteria) return [];
  return [
    ...(criteria.any ?? []).flatMap((branch) => criteriaRequirementText(branch)),
    ...(criteria.age ? [`age ${criteria.age.map(ageLabel).join(" or ")}`] : []),
    ...(criteria.egfr ? [`eGFR ${criteria.egfr.map(egfrLabel).join(" or ")}`] : []),
    ...(criteria.opioid ? [`post-operative IV opioid option ${criteria.opioid.map(opioidLabel).join(" or ")}`] : []),
    ...(criteria.flagsAny ? [`${formatList(criteria.flagsAny.map((flag) => flagLabels[flag] ?? flag))}`] : []),
    ...(criteria.painAtLeast !== undefined ? [`pain score ${criteria.painAtLeast} or above`] : []),
    ...(criteria.cpotAtLeast !== undefined ? [`CPOT ${criteria.cpotAtLeast} or above`] : []),
  ];
}

function ageLabel(age: WorkflowState["age"]) {
  if (age === "under75") return "below 75";
  if (age === "over75") return "75 or older";
  return "unknown";
}

function egfrLabel(egfr: EgfrBand) {
  if (egfr === "gt40") return "above 40";
  if (egfr === "30to40") return "30-40";
  if (egfr === "20to30") return "20-30";
  if (egfr === "lt20") return "below 20 or ESRF";
  return "unknown";
}

function opioidLabel(opioid: OpioidChoice) {
  if (opioid === "unknown") return "not selected";
  return opioid;
}

function buildInterventionBlocks(groups: RecommendationGroup[]): InterventionBlock[] {
  const blockMap = new Map<string, InterventionBlock>();

  for (const group of groups) {
    const key = group.choiceGroup ?? group.id;
    const block = blockMap.get(key) ?? { id: key, choiceGroup: group.choiceGroup, groups: [] };
    block.groups.push(group);
    blockMap.set(key, block);
  }

  return Array.from(blockMap.values())
    .map((block) => ({
      ...block,
      groups: block.groups.sort((a, b) => strengthOrder[a.strength] - strengthOrder[b.strength]),
    }))
    .sort((a, b) => {
      const aNotApplicable = a.groups.every((group) => group.isNotApplicable);
      const bNotApplicable = b.groups.every((group) => group.isNotApplicable);
      if (aNotApplicable !== bNotApplicable) return Number(aNotApplicable) - Number(bNotApplicable);
      const aStrength = Math.min(...a.groups.map((group) => strengthOrder[group.strength]));
      const bStrength = Math.min(...b.groups.map((group) => strengthOrder[group.strength]));
      return aStrength - bStrength;
    });
}

function arePatientDetailsComplete(
  controls: { controls: PatientDetailControl[]; flags: WorkflowFlag[]; allergies: WorkflowFlag[] },
  state: WorkflowState,
) {
  const controlComplete = controls.controls.every((control) => {
    if (control === "age") return state.age !== "unknown";
    if (control === "egfr") return state.egfr !== "unknown";
    if (control === "sedation") return state.sedation !== "unknown";
    if (control === "opioid") return state.opioid !== "unknown";
    if (control === "pain") return state.pain !== null;
    if (control === "cpot") return state.cpot !== null;
    return true;
  });
  const flagsComplete = controls.flags.every((flag) => state.flags.includes(flag) || state.noFlags.includes(flag));
  const allergiesComplete =
    controls.allergies.length === 0 ||
    state.allergies === "no" ||
    (state.allergies === "yes" && controls.allergies.every((flag) => state.flags.includes(flag) || state.noFlags.includes(flag)));

  return controlComplete && flagsComplete && allergiesComplete;
}

function formatList(values: string[]) {
  if (values.length <= 1) return values[0] ?? "";
  return `${values.slice(0, -1).join(", ")} and ${values[values.length - 1]}`;
}

function choiceToken(choiceGroup: string, optionValue: string) {
  return `${choiceGroup}:${optionValue}`;
}

function selectChoice(group: RecommendationGroup, state: WorkflowState, updateState: (patch: NavPatch) => void) {
  if (group.choiceValue) {
    updateState({ opioid: state.opioid === group.choiceValue ? "unknown" : group.choiceValue });
    return;
  }

  if (!group.choiceGroup || !group.optionValue) return;
  const token = choiceToken(group.choiceGroup, group.optionValue);
  const remainingChoices = state.choices.filter((choice) => !choice.startsWith(`${group.choiceGroup}:`));

  updateState({
    choices: state.choices.includes(token) ? remainingChoices : [...remainingChoices, token],
  });
}

function ToolsOverview({ state, navigate }: { state: WorkflowState; navigate: (path: string, patch?: NavPatch) => void }) {
  return (
    <section className="stack">
      <div className="page-heading compact">
        <p className="eyebrow">Tools</p>
        <h1>Scoring and assessment</h1>
        <p>Use the tools directly, or open them from the relevant phase.</p>
      </div>
      <div className="action-grid">
        {toolsNav.map((route) => (
          <button key={route.id} className="route-tile" onClick={() => navigate(route.path, { ...state, route: route.path })}>
            <span>{route.label}</span>
            <small>{route.summary}</small>
          </button>
        ))}
      </div>
      <ReturnToPathway navigate={navigate} state={state} />
    </section>
  );
}

function PainScoreTool({
  state,
  updateState,
  navigate,
}: {
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  navigate: (path: string, patch?: NavPatch) => void;
}) {
  const pain = state.pain;
  return (
    <section className="stack">
      <div className="page-heading compact">
        <p className="eyebrow">Tool</p>
        <h1>NPS / VRS</h1>
        <p>A simple patient-reported pain intensity scale from 0 to 10.</p>
      </div>

      <div className="tool-card">
        <div className="slider-label">
          <span>Pain score</span>
          <strong>{pain ?? "Unknown"}</strong>
        </div>
        <ScoreButtons max={10} value={pain} onChange={(score) => updateState({ pain: score })} />
        <p className="score-output">{pain === null ? "Select a score to show the rating." : painBand(pain)}</p>
      </div>

      <table className="reference-table">
        <thead>
          <tr>
            <th>Score</th>
            <th>Rating</th>
          </tr>
        </thead>
        <tbody>
          {painScaleRows.map((row) => (
            <tr key={row.range}>
              <td>{row.range}</td>
              <td>{row.label}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <ReturnToPathway navigate={navigate} state={state} />
    </section>
  );
}

function FunctionTool({
  state,
  updateState,
  navigate,
}: {
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  navigate: (path: string, patch?: NavPatch) => void;
}) {
  return (
    <section className="stack">
      <div className="page-heading compact">
        <p className="eyebrow">Tool</p>
        <h1>Functional assessment</h1>
        <p>Assess deep breathing, coughing, getting in/out of bed, and mobilising.</p>
      </div>

      <div className="assessment-list">
        {functionalAssessment.map((item) => (
          <article key={item.grade} className="assessment-card">
            <strong>{item.grade}: {item.label}</strong>
            <p>{item.description}</p>
          </article>
        ))}
      </div>

      <Fieldset legend="Patient details">
        <FlagResponseGrid flags={["functionalLimitation"]} state={state} updateState={updateState} />
      </Fieldset>

      <ReturnToPathway navigate={navigate} state={state} />
    </section>
  );
}

function CpotTool({
  state,
  updateState,
  navigate,
}: {
  state: WorkflowState;
  updateState: (patch: NavPatch) => void;
  navigate: (path: string, patch?: NavPatch) => void;
}) {
  const [scores, setScores] = useState<Record<string, number>>({});
  const total = Object.values(scores).reduce((sum, score) => sum + score, 0);

  useEffect(() => {
    if (Object.keys(scores).length > 0) {
      updateState({ cpot: total });
    }
  }, [scores, total, updateState]);

  const patientGroup = state.sedation === "sedated" ? "intubated" : "extubated";
  const indicators = cpotIndicators.filter((indicator) => indicator.patientGroup === "all" || indicator.patientGroup === patientGroup);

  return (
    <section className="stack">
      <div className="page-heading compact">
        <p className="eyebrow">Tool</p>
        <h1>CPOT</h1>
        <p>Critical-Care Pain Observation Tool. Total is scored out of 8.</p>
      </div>

      <div className="score-banner">
        <span>Total</span>
        <strong>{total}/8</strong>
        <small>{total > 2 ? "Above target threshold" : "At or below target threshold"}</small>
      </div>

      <div className="cpot-list">
        {indicators.map((indicator) => (
          <CpotIndicatorControl
            key={indicator.id}
            indicator={indicator}
            value={scores[indicator.id]}
            onChange={(score) => setScores((current) => ({ ...current, [indicator.id]: score }))}
          />
        ))}
      </div>

      <ReturnToPathway navigate={navigate} state={state} />
    </section>
  );
}

function CpotIndicatorControl({
  indicator,
  value,
  onChange,
}: {
  indicator: CpotIndicator;
  value: number | undefined;
  onChange: (score: number) => void;
}) {
  return (
    <fieldset className="cpot-card">
      <legend>{indicator.label}</legend>
      <div className="cpot-button-row">
        {indicator.options.map((option) => (
          <button
            key={option.label}
            type="button"
            className={value === option.score ? "selected" : ""}
            onClick={() => onChange(option.score)}
            title={option.description}
          >
            <strong>{option.score}</strong>
            <span>{option.label}</span>
          </button>
        ))}
      </div>
    </fieldset>
  );
}

function ReturnToPathway({ navigate, state }: { navigate: (path: string, patch?: NavPatch) => void; state: WorkflowState }) {
  return (
    <div className="action-row">
      <button className="icon-text-button primary" onClick={() => navigate(pathForStage(state.stage), state)}>
        <ArrowRight size={18} aria-hidden />
        <span>Return to pathway</span>
      </button>
    </div>
  );
}

function Fieldset({ legend, info, children }: { legend: string; info?: string; children: React.ReactNode }) {
  return (
    <fieldset className="field-group">
      <legend>
        <span>{legend}</span>
        {info && <InfoTip text={info} />}
      </legend>
      {children}
    </fieldset>
  );
}

function SegmentedControl({
  value,
  options,
  onChange,
}: {
  value: string;
  options: Array<[string, string]>;
  onChange: (value: string) => void;
}) {
  return (
    <div className="segmented-control">
      {options.map(([optionValue, label]) => (
        <button
          key={optionValue}
          className={value === optionValue ? "selected" : ""}
          onClick={() => onChange(value === optionValue ? "unknown" : optionValue)}
          type="button"
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function strengthLabel(strength: RecommendationStrength) {
  if (strength === "recommended") return "Recommended";
  if (strength === "consider") return "To be considered";
  if (strength === "caution") return "Use with caution";
  return "Escalation";
}

function pathForStage(stage: WorkflowState["stage"]) {
  if (stage === "unknown") {
    return "/before-surgery";
  }
  if (stage === "breakthrough-pain") {
    return "/oral-analgesia";
  }

  const route = workflowRoutes.find((item) => item.id === stage);
  return route?.path ?? "/before-surgery";
}

function nextStepForTarget(groupId: string) {
  const nextRouteByTargetId: Record<string, WorkflowStageId> = {
    "before-surgery-next-phase": "during-surgery",
    "during-surgery-next-phase": "handover",
    "handover-next-phase": "itu-sedated",
    "itu-sedated-next-phase": "after-extubation",
    "safe-for-oral-analgesia": "oral-analgesia",
  };
  const nextRouteId = nextRouteByTargetId[groupId];
  return nextRouteId === undefined ? undefined : workflowNav.find((route) => route.id === nextRouteId);
}
